import { colors } from "@indeed/ifl-tokens";
import React from "react";
import { StyleSheet } from "react-native";

import { Flex, Box, Heading, Location } from "../../../../../../mads";
import { useI18n } from "../../../../../../shared/I18n";
import { DwellTrackedSection } from "../../../../context/DwellTrackedSection";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";
import { useTrueJobLocation } from "../../../../utils/locationUtils";
import { ViewJobDivider } from "../../Divider";
import { LocationSection } from "../LocationSection";

const styles = StyleSheet.create({
  headingSx: {
    marginBottom: 12,
    fontSize: 20,
    fontWeight: "bold",
    color: colors.neutral["1000"],
  },
  sectionItemKeySx: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
  },
  sectionIconSx: {
    color: colors.neutral["700"],
    height: 20,
    width: 20,
    marginRight: 24,
  },
  locationStringSx: {
    flexShrink: 1,
    marginLeft: 12,
    color: colors.neutral["1000"],
  },
  sectionSx: {
    borderBottomWidth: 1,
    borderBottomColor: colors.neutral["300"],
    paddingTop: 0,
    paddingLeft: 14,
    marginHorizontal: -14,
  },
  wrapperStyle: {
    borderBottomColor: colors.neutral["300"],
    marginVertical: 24,
  },
});

interface LocationModel {
  formattedStreetAddress?: string;
  jobLatitude?: number;
  jobLongitude?: number;
}

interface JobLocationSectionProps {
  formattedLocation?: string;
  location?: LocationModel;
  loggedIn?: boolean;
  isCommuteEstimateActive?: boolean;
  isSimpleVjImproveActive?: boolean;
}

export const JobLocationSection = ({
  formattedLocation,
  location,
  loggedIn = false,
  isCommuteEstimateActive = false,
  isSimpleVjImproveActive = false,
}: JobLocationSectionProps) => {
  const { gettext } = useI18n();
  const trueJobLocation = useTrueJobLocation();

  if (!formattedLocation && !location) {
    return null;
  }

  const renderCommuteEstimate = !!(
    !trueJobLocation &&
    loggedIn &&
    isCommuteEstimateActive &&
    location?.jobLatitude &&
    location?.jobLongitude
  );

  const locationHeader = (
    <Heading
      level={2}
      id="jobLocationSectionTitle"
      sx={{
        ...styles.headingSx,
      }}
    >
      {gettext("Location")}
    </Heading>
  );

  const locationContent = (
    <Flex sx={styles.sectionItemKeySx}>
      <Box sx={{ paddingRight: 8, top: 2 }}>
        <Location fill={colors.neutral["700"]} size="sm" />
      </Box>
      <Box sx={styles.locationStringSx}>
        {renderCommuteEstimate && <Heading level={2}>{gettext("Job address")}</Heading>}
        <LocationSection />
      </Box>
    </Flex>
  );

  return (
    <>
      <DwellTrackedSection section="location">
        <Box sx={styles.wrapperStyle}>
          <Box sx={{ paddingRight: 16 }}>
            {locationHeader}
            {locationContent}
          </Box>
        </Box>
      </DwellTrackedSection>
      {isSimpleVjImproveActive && <ViewJobDivider />}
    </>
  );
};

export const JobLocationSectionOneGraphWrapper = () => {
  const viewjobOneGraphModel = useViewjobOneGraphModel();

  if (!viewjobOneGraphModel) {
    return null;
  }

  const { job, features } = viewjobOneGraphModel;
  const { location, formattedLocation } = job;
  const { isSimpleVjImproveActive, isCommuteEstimateActive } = features;

  return (
    <JobLocationSection
      formattedLocation={formattedLocation}
      location={location}
      loggedIn={viewjobOneGraphModel.jobSeeker.loggedIn}
      isCommuteEstimateActive={isCommuteEstimateActive}
      isSimpleVjImproveActive={isSimpleVjImproveActive}
    />
  );
};
